import { Delete, History, Close, Search } from "@mui/icons-material";
import { FormHelperText, ListItemIcon, Menu, MenuItem } from "@mui/material";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDeleteRunById, useGetRunsCountForWorkflow, useGetRunsForWorkflow, usePaginationValues } from "../../api/workflowsApi";
import { IdValueOption } from "../../types/common";
import { Run } from "../../types/workflow";
import HorizontalTabs from "../Common/HorizontalTabs";
import LoadingState from "../Common/LoadingState";
import WorkflowHistoryDetails from "./WorkflowHistoryDetails";
import WorkflowHistoryTable from "./WorkflowHistoryTable";

const WorkflowHistory = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchRunId, setSearchRunId] = useState<string>('');
    const { data: workflowHistory, isLoading } = useGetRunsForWorkflow(searchRunId.trim() || undefined);
    const { mutate: deleteRunById } = useDeleteRunById();
    const [selectedRow, setSelectedRow] = useState<null | Run>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpenedForId, setMenuOpenedForId] = useState('');
    const {data : runsAggregation } = useGetRunsCountForWorkflow();
    const [searchParams, setSearchParams] = useSearchParams();
    const {page, limit, state} = usePaginationValues();
    const [tabValue, setTabValue] = useState(state || "all");

    const [resultsPerPage, setResultsPerPage] = useState(limit);

    const allCount = runsAggregation?.total || 0;
    const waitingCount = runsAggregation?.pending || 0;
    const failedCount = runsAggregation?.failed || 0;
    const completedCount = runsAggregation?.completed || 0;
    const filteredCount = runsAggregation?.filtered || 0;
    const totalPages = useMemo(() => {
        let resultsCount = runsAggregation?.total;
        switch (tabValue) {
            case 'waiting':
                resultsCount = runsAggregation?.pending;
                break;
            case 'completed':
                resultsCount = runsAggregation?.completed;
                break;
            case 'filtered':
                resultsCount = runsAggregation?.filtered;
                break;
            case 'failed':
                resultsCount = runsAggregation?.failed;
                break;
        }
        return Math.ceil((resultsCount || resultsPerPage) / resultsPerPage)
    }, [runsAggregation, resultsPerPage, tabValue]);
    const [currentPage, setCurrentPage] = useState(page);


    useEffect(() => {
        if (page > totalPages && totalPages !== 1) {
            searchParams.set('page', totalPages.toString());
            setCurrentPage(totalPages);
            setSearchParams(searchParams);
        }
    }, [totalPages]);

    const tabs: IdValueOption[] = [
        {
            id: 'all',
            value: 'All runs',
            count: allCount,
        },
        {
            id: 'waiting',
            value: 'Waiting',
            count: waitingCount,
        },
        {
            id: 'failed',
            value: 'Failed',
            count: failedCount,
        },
        {
            id: 'filtered',
            value: 'Filtered',
            count: filteredCount,
        },
        {
            id: 'completed',
            value: 'Completed',
            count: completedCount,
        },
    ]

    const handleCurrentPageChange = (page : number) => {
        setCurrentPage(page);
        searchParams.set('page', page.toString());
        setSearchParams(searchParams);
    }

    const handleTabChange = (tabId : string) => {
        setTabValue(tabId);
        searchParams.set('state', tabId);
        setSearchParams(searchParams);
    }

    const handleResultsPerPageChange = (resultsPerPage : number) => {
        setResultsPerPage(resultsPerPage);
        searchParams.set('limit', resultsPerPage.toString());
        setSearchParams(searchParams);
    }

    useEffect(() => {
        window.history.replaceState({}, '');
    }, [location.state?.history]);

    return (
        <>
            <div className="bg-backgroundGrey w-[calc(100%_-_240px)] h-full flex flex-col overflow-hidden">
                {/* Header Section */}
                <div className="bg-white border-b border-borderGrey px-8 py-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-backgroundBlue rounded-lg">
                                <History className="text-nanonetsBlue" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-semibold text-greyishBlueMid mb-1">Run History</h1>
                                <p className="text-sm text-greyishBlue">
                                    {allCount} {allCount === 1 ? 'run' : 'runs'} total
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Tabs and Search Box - Inline, right-aligned */}
                    <div className="flex items-center justify-between">
                        <HorizontalTabs
                            tabArray={tabs}
                            tabValue={tabValue}
                            setTabValue={handleTabChange}
                        />
                        {/* Search Box - Right aligned, inline with tabs */}
                        <div className="relative w-[380px]">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                                <Search className="text-greyishBlue text-base" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search by Run ID..."
                                value={searchRunId}
                                onChange={(e) => setSearchRunId(e.target.value)}
                                className="w-full pl-10 pr-10 py-2 border border-borderGrey rounded-lg text-sm text-greyishBlueMid
                                           focus:outline-none focus:ring-2 focus:ring-nanonetsBlue focus:border-transparent
                                           placeholder:text-greyishBlue transition-all shadow-sm hover:shadow-md"
                            />
                            {searchRunId && (
                                <button
                                    onClick={() => setSearchRunId('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full
                                               hover:bg-backgroundBlue text-greyishBlue hover:text-greyishBlueMid
                                               transition-all z-10"
                                    aria-label="Clear search"
                                >
                                    <Close className="text-sm" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 overflow-hidden bg-white">
                    {workflowHistory?.length && Array.isArray(workflowHistory) ? (
                        <div className="overflow-auto bg-white w-full h-full">
                            <WorkflowHistoryTable
                                workflowHistory={workflowHistory}
                                selectedRow={selectedRow}
                                onHistoryRowClick={(row) => {
                                    setIsDetailOpen(true);
                                    setSelectedRow(row);
                                }}
                                menuOpenedForId={menuOpenedForId}
                                setMenuOpenedForId={setMenuOpenedForId}
                                setMenuAnchorEl={setMenuAnchorEl}
                            />
                        </div>
                    ) : (
                        <div className="flex items-center justify-center w-full h-full">
                            {isLoading ? (
                                <LoadingState />
                            ) : (
                                <div className="flex flex-col items-center justify-center min-h-[400px]">
                                    <div className="text-lg font-semibold text-greyishBlueMid mb-2">No runs found</div>
                                    <p className="text-sm text-greyishBlue text-center max-w-md">
                                        {searchRunId 
                                            ? `No run found with ID: ${searchRunId}`
                                            : tabValue === 'all' 
                                                ? 'No workflow runs have been executed yet.'
                                                : `No ${tabValue} runs found.`}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination Footer */}
                {workflowHistory?.length && Array.isArray(workflowHistory) && !searchRunId && (
                    <div className="bg-white border-t border-borderGrey px-8 py-4 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-greyishBlue">Results per page:</span>
                            <select
                                className="border border-borderGrey rounded-lg px-3 py-1.5 text-sm text-greyishBlueMid
                                           focus:outline-none focus:ring-2 focus:ring-nanonetsBlue focus:border-transparent
                                           bg-white cursor-pointer transition-all"
                                value={resultsPerPage}
                                onChange={(e) => handleResultsPerPageChange(parseInt(e.target.value) || resultsPerPage)}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-greyishBlue">
                                Page <span className="font-medium text-greyishBlueMid">{currentPage}</span> of <span className="font-medium text-greyishBlueMid">{totalPages}</span>
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    className="px-4 py-1.5 border border-borderGrey rounded-lg text-sm text-greyishBlueMid
                                               hover:bg-backgroundBlue hover:border-nanonetsBlue/30
                                               disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-borderGrey
                                               transition-all font-medium"
                                    onClick={() => currentPage > 1 && handleCurrentPageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <input
                                    type="number"
                                    min="1"
                                    max={totalPages}
                                    className="border border-borderGrey rounded-lg px-3 py-1.5 w-20 text-sm text-greyishBlueMid text-center
                                               focus:outline-none focus:ring-2 focus:ring-nanonetsBlue focus:border-transparent"
                                    value={currentPage}
                                    onChange={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= totalPages) {
                                            handleCurrentPageChange(page);
                                        }
                                    }}
                                />
                                <button
                                    className="px-4 py-1.5 border border-borderGrey rounded-lg text-sm text-greyishBlueMid
                                               hover:bg-backgroundBlue hover:border-nanonetsBlue/30
                                               disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-borderGrey
                                               transition-all font-medium"
                                    onClick={() => currentPage < totalPages && handleCurrentPageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Backdrop - Full Page */}
            {isDetailOpen && (
                <div 
                    className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300"
                    onClick={() => {
                        setIsDetailOpen(false);
                        setSelectedRow(null);
                    }}
                />
            )}

            {/* Slide-over Panel - Full Page */}
            <div className={clsx(
                "fixed top-0 right-0 h-screen bg-white shadow-2xl z-50 transition-transform duration-300 ease-in-out",
                "w-[500px] max-w-[90vw]",
                isDetailOpen ? "translate-x-0" : "translate-x-full"
            )}>
                {selectedRow && isDetailOpen && (
                    <WorkflowHistoryDetails
                        selectedRun={selectedRow}
                        closeDetails={() => {
                            setIsDetailOpen(false);
                            setSelectedRow(null);
                        }}
                    />
                )}
            </div>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                disableAutoFocusItem
                onClose={() => setMenuAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    className: "!shadow-lg !border !border-borderGrey !rounded-lg !mt-1"
                }}
            >
                <MenuItem
                    onClick={() => {
                        setMenuAnchorEl(null);
                        menuOpenedForId && deleteRunById({runId: menuOpenedForId});
                        setMenuOpenedForId('');
                    }}
                    className="!text-xs !py-2 !px-3 !text-[#C05353] hover:!bg-red-50"
                >
                    <ListItemIcon className="mr-2 !min-w-0">
                        <Delete className="!text-sm" htmlColor="#C05353" />
                    </ListItemIcon>
                    Delete run
                </MenuItem>
            </Menu>
        </>
    )
}

export default WorkflowHistory;